import type { AxiosInstance } from 'axios';
import { getAuthToken } from './get-token';
import { getSessionType } from './session-type';
import { terminalLogout } from './terminal-logout';
import {
  currentToken as readWebinappToken,
  recoverWebinappSession,
} from './webinapp-auth-interceptor';
import { webviewHttpClient as mHttpClient } from '@/app/[locale]/(m)/components/auth/webview-http-client';
import { webviewHttpClient as orderHttpClient } from '@/app/[locale]/(order)/components/auth/webview-http-client';
import { useNewAuthStore } from '@/store/use-new-auth-store';

/**
 * Auth cho các API đi qua `webviewHttpClient` (đặt đơn, ước tính phí, địa chỉ,
 * rating...) — KHÔNG qua `publicHttpClient` nên cần xử lý riêng. Cài cho CẢ web
 * lẫn webinapp; rẽ nhánh theo `session_type` (nguồn sự thật) bên trong.
 *
 * Request: gắn Bearer theo loại phiên (webviewHttpClient là axios trần, không tự
 * có token). `getAuthToken` đã rẽ theo session_type (web→cookie, webinapp→token bền).
 *
 * Response 401:
 *  - web       → terminalLogout (token JWT hết hạn — không refresh).
 *  - webinapp  → resync (activate lại) + retry 1 lần → vẫn 401 → cờ webinappExpired
 *                (dialog "mở lại từ app"); GIỮ phiên, KHÔNG logout.
 *  - ẩn danh   → reject im lặng (API cần đăng nhập, không phải "hết hạn").
 *
 * Luồng FB `/m` (không có session_type, dùng session.access_token của
 * WebviewSessionProvider) → getSessionType()=null → nhánh ẩn danh (reject), không
 * đụng; provider của `/m` tự xử lý.
 */

let installed = false;

export function installWebviewAuthInterceptor() {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  attachWebviewAuth(mHttpClient);
  attachWebviewAuth(orderHttpClient);
}

function attachWebviewAuth(client: AxiosInstance) {
  client.interceptors.request.use((config) => {
    if (config.headers && !config.headers.Authorization) {
      const token = getAuthToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  });

  client.interceptors.response.use(
    (res) => res,
    async (error) => {
      const status = error?.response?.status;
      const original = error?.config;
      if (status !== 401 || !original) {
        return Promise.reject(error);
      }

      const type = getSessionType();

      if (type === 'web') {
        terminalLogout();
        return Promise.reject(error);
      }

      if (type === 'webinapp') {
        // Đã resync + retry vẫn 401 → token hỏng thật → dialog reopen (giữ phiên).
        if (original._retry) {
          useNewAuthStore.getState().setWebinappExpired(true);
          return Promise.reject(error);
        }
        original._retry = true;
        const ok = await recoverWebinappSession();
        if (!ok) {
          useNewAuthStore.getState().setWebinappExpired(true);
          return Promise.reject(error);
        }
        const token = readWebinappToken();
        if (token && original.headers) {
          original.headers.Authorization = `Bearer ${token}`;
        }
        return client.request(original);
      }

      // Ẩn danh (session_type=null) → reject im lặng.
      return Promise.reject(error);
    }
  );
}
